import React, { useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';

import {
  filterStyles as styles,
  FilterTextInput,
  FilterSelect,
  FilterNativeSelect,
  FilterRange,
} from 'app/src/elements/Filter/inlineFilters';
import { ASSET_FILTER_OPTIONS } from './queries';
import {
  sellerOptions,
  buyerOptions,
  stringOptions,
} from 'app/src/components/Reports/filterOptions';
import {
  TITLE_LOG_STATUSES,
  TITLE_STATUS_MISSING,
} from 'app/src/components/TitleLogs/constants';

export const SOLD_STATUS_SCOPES = { sold: 'sold', notSold: 'unsold' };

export const TRANSMISSION_KINDS = {
  automatic: 'automatic',
  manual: 'manual',
};

// Each bound is its own filter column, so an open-ended range works too.
export const SOLD_ON_FROM = 'soldOnFrom';
export const SOLD_ON_TO = 'soldOnTo';

const TITLE_STATUS_OPTIONS = stringOptions(
  [TITLE_STATUS_MISSING, ...TITLE_LOG_STATUSES]
);

const IndexFilters = ({ filters, setFilter, onClear }) => {
  const [showAdvanced, setShowAdvanced] = useState(false);
  const { data } = useQuery(ASSET_FILTER_OPTIONS);

  const options = useMemo(() => {
    const queryData = data || {};

    return {
      sellers: sellerOptions(queryData),
      buyers: buyerOptions(queryData),
      makes: stringOptions(queryData.selectOptions?.assetMakes),
      models: stringOptions(queryData.selectOptions?.assetModels),
      engineMfrs: stringOptions(queryData.selectOptions?.assetEngineMfrs),
    };
  }, [data]);

  const range = (label, minKey, maxKey) => (
    <FilterRange
      label={label}
      minValue={filters[minKey]}
      maxValue={filters[maxKey]}
      onChangeMin={value => setFilter(minKey, value)}
      onChangeMax={value => setFilter(maxKey, value)}
    />
  );

  return (
    <div style={styles.container}>
      <div style={styles.grid}>
        <FilterTextInput
          label="Search"
          value={filters.search}
          onChange={value => setFilter('search', value)}
        />
        <FilterSelect
          label="Seller"
          options={options.sellers}
          value={filters.sellerId}
          onChange={value => setFilter('sellerId', value)}
        />
        <FilterSelect
          label="Buyer"
          options={options.buyers}
          value={filters.buyerId}
          onChange={value => setFilter('buyerId', value)}
        />
        <FilterSelect
          label="Make"
          options={options.makes}
          value={filters.make}
          onChange={value => setFilter('make', value)}
        />
        <FilterSelect
          label="Model"
          options={options.models}
          value={filters.model}
          onChange={value => setFilter('model', value)}
        />
        <FilterNativeSelect
          label="Sold Status"
          value={filters.soldStatus}
          onChange={value => setFilter('soldStatus', value)}
        >
          <option value="">Sold + Not Sold</option>
          <option value={SOLD_STATUS_SCOPES.sold}>Sold</option>
          <option value={SOLD_STATUS_SCOPES.notSold}>Not Sold</option>
        </FilterNativeSelect>
      </div>

      {showAdvanced &&
        <div style={styles.advancedGrid}>
          <FilterTextInput
            label="Vin"
            value={filters.vin}
            onChange={value => setFilter('vin', value)}
          />
          <FilterTextInput
            label="Stock #"
            value={filters.stockNumber}
            onChange={value => setFilter('stockNumber', value)}
          />
          <FilterTextInput
            label="Client #"
            value={filters.clientNumber}
            onChange={value => setFilter('clientNumber', value)}
          />
          <FilterSelect
            label="Title Status"
            options={TITLE_STATUS_OPTIONS}
            value={filters.cachedTitleStatus}
            onChange={value => setFilter('cachedTitleStatus', value)}
          />
          <FilterNativeSelect
            label="Inspection Complete"
            value={filters.inspectionCompleted}
            onChange={value => setFilter('inspectionCompleted', value)}
          >
            <option value="">Yes + No</option>
            <option value="true">Yes</option>
            <option value="false">No</option>
          </FilterNativeSelect>
          <FilterSelect
            label="Engine Mfr"
            options={options.engineMfrs}
            value={filters.engineMfr}
            onChange={value => setFilter('engineMfr', value)}
          />
          <FilterNativeSelect
            label="Transmission"
            value={filters.transmission}
            onChange={value => setFilter('transmission', value)}
          >
            <option value="">Any</option>
            <option value={TRANSMISSION_KINDS.automatic}>Automatic</option>
            <option value={TRANSMISSION_KINDS.manual}>Manual</option>
          </FilterNativeSelect>
          {range('Year', 'yearMin', 'yearMax')}
          {range('Miles', 'milesMin', 'milesMax')}
          {range('Sold Price', 'soldPriceMin', 'soldPriceMax')}
          <FilterRange
            label="Sold Between"
            type="date"
            wide
            minValue={filters[SOLD_ON_FROM]}
            maxValue={filters[SOLD_ON_TO]}
            onChangeMin={value => setFilter(SOLD_ON_FROM, value)}
            onChangeMax={value => setFilter(SOLD_ON_TO, value)}
          />
        </div>
      }

      <div style={styles.footer}>
        <button
          type="button"
          style={styles.linkButton}
          onClick={() => setShowAdvanced(!showAdvanced)}
        >
          {showAdvanced ? 'Fewer search options' : 'More search options'}
        </button>
        <button
          type="button"
          style={styles.linkButton}
          onClick={onClear}
        >
          Clear filters
        </button>
      </div>
    </div>
  );
};

export default IndexFilters;
